/**
 * Sustainability data collected in CSRD.
 *
 * Proxied by the API, which forwards the caller's own bearer token to CSRD.
 * CSRD then decides, from that token alone, which client's data it is and
 * what this person may read of it. The browser never learns where CSRD lives
 * and never holds a credential for it.
 *
 * The reporting is the one the person has selected in CSRD. It is read here
 * and never changed: choosing another one is done in CSRD, where it also
 * changes what they see there.
 */

import { http, type RequestOptions } from '@/api/http'

export interface CsrdUser {
  readonly id: string
  readonly name: string
  readonly email: string
}

export interface CsrdReporting {
  readonly id: number
  readonly code: string
  readonly label: string
  readonly year: string
}

export interface CsrdEntity {
  readonly id: number
  readonly code: string
  readonly label: string
}

export interface CsrdContext {
  readonly user: CsrdUser
  /** Null when the client has no reporting at all. */
  readonly reporting: CsrdReporting | null
  readonly entities: readonly CsrdEntity[]
}

/** Where CSRD's own screens are; empty when the deployment did not say. */
export interface CsrdWeb {
  readonly url: string
}

export function csrdWeb(options: RequestOptions = {}): Promise<CsrdWeb> {
  return http.get<CsrdWeb>('/csrd/web', options)
}

/** A disclosure as CSRD's collection lists it. */
export interface CsrdDisclosureSummary {
  readonly id: number
  readonly code: string
  readonly label: string
  /** The standard: `E1`, `ESRS2`, `S1`. */
  readonly standard: string
  readonly standardLabel: string
  /** The requirement it answers, as a report tags it: `E1-6`, `GOV-1`. */
  readonly ref: string
  /** How much of it has been filled in, 0 to 100, as CSRD last measured it. */
  readonly progress: number
  /** CSRD's own status numbers for the collection, its control and its audit. */
  readonly collected?: number
  readonly controlled?: number
  readonly audited?: number
}

export interface CsrdCell {
  readonly value: string
  readonly numeric: boolean
  readonly strong: boolean
  /** A background from a design template, or empty. Validated before use. */
  readonly fill?: string
  /** An alignment from a design template, or empty. Validated before use. */
  readonly align?: string
}

/** A narrative or a table, in the order the disclosure presents them. */
export interface CsrdBlock {
  readonly kind: 'text' | 'table'
  readonly title: string
  /** For a narrative: as collected, and not yet sanitised. */
  readonly html: string
  readonly header: readonly CsrdCell[]
  readonly rows: readonly (readonly CsrdCell[])[]
}

export interface CsrdDisclosure extends CsrdDisclosureSummary {
  readonly blocks: readonly CsrdBlock[]
}

/** What was read, and from where, so the report can say so. */
export interface CsrdContent {
  readonly reporting: CsrdReporting | null
  readonly entity: CsrdEntity
  readonly pulledAt: string
  readonly disclosures: readonly CsrdDisclosure[]
}

/** A reporting CSRD lists. */
export interface CsrdReportingInfo {
  readonly id: number
  readonly code: string
  readonly label: string
  readonly year: string
  /** `CSRD`, `VSME`, `CBCR`… */
  readonly kind: string
  /** The reporting CSRD links as its N-1, when it links one. */
  readonly n1Id: number | null
  readonly n1Code: string
}

export interface CsrdReportings {
  /** The reporting the person is on in CSRD now, when they are on one. */
  readonly currentId: number | null
  readonly reportings: readonly CsrdReportingInfo[]
}

/**
 * Which reporting a read is about, and the document's principal one.
 *
 * CSRD answers for the reporting the person is on there. A secondary reporting
 * is read by moving them onto it for the length of the read and putting the
 * principal one back; the principal one is read only while CSRD is on it.
 */
export interface CsrdReadingOf {
  readonly reportingCode: string
  readonly principalCode: string
}

/** The person, the reporting they are working on in CSRD, and its entities. */
export function csrdContext(language: string, options: RequestOptions = {}): Promise<CsrdContext> {
  return http.get<CsrdContext>('/csrd/context', { ...options, query: { language } })
}

/** The disclosures collected for one entity. */
export function csrdDisclosures(
  entityId: number,
  language: string,
  options: RequestOptions = {},
  reading?: CsrdReadingOf,
): Promise<CsrdDisclosureSummary[]> {
  return http.get<CsrdDisclosureSummary[]>('/csrd/disclosures', {
    ...options,
    query: { entityId, language, ...(reading ? { ...reading } : {}) },
  })
}

/**
 * What the chosen disclosures say, as text and tables.
 *
 * Slow against a large statement: a presentation tree is read per disclosure.
 * Pass a signal so a dialog closed mid-read does not leave the work running.
 */
export function csrdContent(
  entityId: number,
  roleTypeIds: readonly number[],
  language: string,
  options: RequestOptions = {},
  reading?: CsrdReadingOf,
): Promise<CsrdContent> {
  return http.post<CsrdContent>(
    '/csrd/disclosures/content',
    { entityId, roleTypeIds, ...(reading ? { ...reading } : {}) },
    { ...options, query: { language } },
  )
}

/** A table read again from CSRD, to compare with the report's and to refresh it from. */
export interface CsrdTablePull {
  readonly reporting: CsrdReporting | null
  readonly entity: CsrdEntity
  readonly disclosure: CsrdDisclosureSummary
  readonly table: CsrdBlock
  readonly pulledAt: string
}

/** What names a table in CSRD: the part of a report's link that CSRD is asked with. */
export interface CsrdTableRef {
  readonly entityId: number
  readonly roleTypeId: number
  readonly roleCode: string
  readonly tableIndex: number
  readonly reportingCode: string
  /** The document's principal reporting, put back after a secondary one is read. */
  readonly principalCode?: string
}

/**
 * A table imported earlier, as CSRD holds it now.
 *
 * Refused (409, `csrd_other_reporting`) when the person has another reporting
 * selected in CSRD than the one the table was read from. It is never changed
 * from here.
 */
export function csrdTablePull(
  ref: CsrdTableRef,
  language: string,
  options: RequestOptions = {},
): Promise<CsrdTablePull> {
  return http.post<CsrdTablePull>('/csrd/tables/pull', ref, { ...options, query: { language } })
}

/** Every reporting the client has in CSRD, and the one the person is on. */
export function csrdReportings(options: RequestOptions = {}): Promise<CsrdReportings> {
  return http.get<CsrdReportings>('/csrd/reportings', options)
}

/**
 * Move the person onto a reporting in CSRD.
 *
 * A setting of theirs there, what CSRD's own screens open on, so it is done
 * only when they ask: from the button that makes a document's reporting the
 * current one.
 */
export function csrdSetCurrentReporting(
  reportingId: number,
  options: RequestOptions = {},
): Promise<CsrdReporting> {
  return http.post<CsrdReporting>('/csrd/reporting/current', { reportingId }, options)
}
